import * as React from 'react';

import { SimpleTreeView } from '@mui/x-tree-view';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { WSTestingTreeValue } from '../../types/WSTestingData';
import { WorkshopTestResult } from '../../types/WorkshopResult';

import { FolderLabel } from '../../subs/FolderLabel';
import { StyledTreeItem } from '../../subs/StyledTreeItem';
import { useWSTestingChipFilter } from './WSTestingChipFilterContext';

// Maps a chip's semantic slot (as used by FolderLabel) to the underlying WorkshopTestResult
// shown by that chip. Note 'errors' intentionally maps to Failed (not the *NotSupported values).
const chipResultTypeMap: Record<'passed' | 'warnings' | 'errors' | 'info', WorkshopTestResult> = {
    passed: WorkshopTestResult.Passed,
    warnings: WorkshopTestResult.Warning,
    errors: WorkshopTestResult.Failed,
    info: WorkshopTestResult.NotComplete
};

const productSelectHandlerMaker = (handler: (nodeHierarchy: string[]) => void) => {
    function onSelectHandler(event: React.SyntheticEvent<Element, Event>, nodeIds: string[] | string, isSelected: boolean): void {
        if (isSelected) handler((nodeIds as string).split('<>'));
    }

    return onSelectHandler;
}

export const WSTestingProdTree = ({ treeValues, onSelectHandler }:
    {
        treeValues: WSTestingTreeValue[],
        onSelectHandler: (nodeIds: string[]) => void
    }) => {
    // filter the tree to only have the top level products (testCombinationId === 0)
    const leveltreeValues = treeValues.filter(tv => tv.key.testCombinationId === 0);
    // then filter to exclude these products, to pass to the next level of the tree
    const lowertreeValues = treeValues.filter(tv => tv.key.testCombinationId !== 0);

    return <Box minWidth={'35%'}>
        {
            <SimpleTreeView slots={{ collapseIcon: RemoveIcon, expandIcon: AddIcon }} onItemSelectionToggle={productSelectHandlerMaker(onSelectHandler)}>
                {leveltreeValues &&
                    leveltreeValues.map(pc =>
                        <TTVTopLevelProducts treeValue={pc} treeValues={lowertreeValues} />)}
            </SimpleTreeView>
        }
    </Box>
}

const TTVTopLevelProducts = ({ treeValue, treeValues }:
    {
        treeValue: WSTestingTreeValue,
        treeValues: WSTestingTreeValue[]
    }) => {
        // filter the tree to only include the current product, to pass to the next level of the tree
    const lowertreeValues = treeValues.filter(tv => tv.key.wsProdUpperId === treeValue.key.wsProdUpperId && tv.key.wsTreeEntryType !== 0);
    // then filter to only have the consume and provider levels (testCombinationId !== 0 and wSTreeEntryType === 0)
    const leveltreeValues = treeValues.filter(tv => tv.key.wsProdUpperId === treeValue.key.wsProdUpperId && tv.key.wsTreeEntryType === 0);

    const itemIdValue = treeValue.key.wsProdUpperId?.toString() ?? 'error';
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    return <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed} />}>
        {leveltreeValues && leveltreeValues.map(tcombo =>
            <TTVTestCombos treeValue={tcombo} treeValues={lowertreeValues} itemIdValueIn={itemIdValue} />
        )}
    </StyledTreeItem>
}

const TTVTestCombos = ({ treeValue, treeValues, itemIdValueIn }:
    {
        treeValue: WSTestingTreeValue,
        treeValues: WSTestingTreeValue[],
        itemIdValueIn: string
    }) => {
    // filter the tree to only include this test combination, to pass to the next
    const lowertreeValues = treeValues.filter(tv => tv.key.testCombinationId === treeValue.key.testCombinationId && tv.key.interfaceId !== 0);
    // then filter for current level (same test combination)
    const leveltreeValues = treeValues.filter(tv => tv.key.testCombinationId === treeValue.key.testCombinationId && tv.key.interfaceId === 0);
    const itemIdValue = `${itemIdValueIn}<>${treeValue.key.testCombinationId ?? 'error'}`;
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    return lowertreeValues && lowertreeValues.length > 0 ? <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed} />}>
        {leveltreeValues && leveltreeValues.map(tcombo =>
            <TTVTCProvConsTree treeValue={tcombo} treeValues={lowertreeValues} itemIdValueIn={itemIdValue} />
        )}
    </StyledTreeItem> : null
}

const TTVTCProvConsTree = ({ treeValue, treeValues, itemIdValueIn }:
    {
        treeValue: WSTestingTreeValue,
        treeValues: WSTestingTreeValue[],
        itemIdValueIn: string
    }) => {
    // filter the tree to only include this nodes data, to pass to the next
    const lowertreeValues = treeValues.filter(tv => tv.key.wsTreeEntryType === treeValue.key.wsTreeEntryType && tv.key.companyId !== 0);
    // then filter for current level (same Provider / consumer)
    const leveltreeValues = treeValues.filter(tv => tv.key.wsTreeEntryType === treeValue.key.wsTreeEntryType && tv.key.companyId === 0);
    const itemIdValue = `${itemIdValueIn}<>${treeValue.key.wsTreeEntryType ?? 'error'}`;
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    return lowertreeValues && lowertreeValues.length > 0 ? <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed} />}>
        {leveltreeValues && leveltreeValues.map(tcombo =>
            <TTVInterfaceTree treeValue={tcombo} treeValues={lowertreeValues} itemIdValueIn={itemIdValue} />
        )}
    </StyledTreeItem> : null
}

const TTVInterfaceTree = ({ treeValue, treeValues, itemIdValueIn }:
    {
        treeValue: WSTestingTreeValue,
        treeValues: WSTestingTreeValue[],
        itemIdValueIn: string
    }) => {
    // filter the tree to only include this nodes data, to pass to the next
    const lowertreeValues = treeValues.filter(tv => tv.key.interfaceId === treeValue.key.interfaceId && tv.key.wsProdLowerId !== 0);
    // then filter for current level (same Provider / consumer)
    const leveltreeValues = treeValues.filter(tv => tv.key.interfaceId === treeValue.key.interfaceId && tv.key.wsProdLowerId === 0);
    const itemIdValue = `${itemIdValueIn}<>${treeValue.key.interfaceId ?? 'error'}`;
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    return lowertreeValues && lowertreeValues.length > 0 ? <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed} />}>
        {leveltreeValues && leveltreeValues.map(tcombo =>
            <TTVLowerProductsTree treeValue={tcombo} treeValues={lowertreeValues} itemIdValueIn={itemIdValue} />
        )}
    </StyledTreeItem> : null
}

const TTVLowerProductsTree = ({ treeValue, treeValues, itemIdValueIn }:
    {
        treeValue: WSTestingTreeValue,
        treeValues: WSTestingTreeValue[],
        itemIdValueIn: string
    }) => {
    // then filter for current level (same Provider / consumer)
    const leveltreeValues = treeValues.filter(tv => tv.key.companyId === treeValue.key.companyId);
    const itemIdValue = `${itemIdValueIn}<>${treeValue.key.companyId ?? 'error'}`;
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    return <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed} />}>
        {leveltreeValues && leveltreeValues.map(tcombo =>
            <TTVLowerProductsItem treeValue={tcombo} itemIdValueIn={itemIdValue} />
        )}
    </StyledTreeItem>
}

const TTVLowerProductsItem = ({ treeValue, itemIdValueIn }:
    {
        treeValue: WSTestingTreeValue,
        itemIdValueIn: string
    }) => {
    // then filter for current level (same Provider / consumer)
    const itemIdValue = `${itemIdValueIn}<>${treeValue.key.wsProdLowerId ?? 'error'}`;
    let color: "inherit" | "error" | "info" | "success" | "warning" = "inherit";
    if (treeValue.testsFailed > 0) color = "error";
    else if (treeValue.testsWarning > 0) color = "warning";
    else if (treeValue.testsNotCompleted > 0) color = "info";
    else if (treeValue.testsPassed > 0) color = "success";

    const { selectResultFilter } = useWSTestingChipFilter();
    const chipClickHandler = (resultType: 'passed' | 'warnings' | 'errors' | 'info', event: React.MouseEvent) => {
        event.stopPropagation(); // avoid double-handling via the tree's own selection toggle
        selectResultFilter(itemIdValue.split('<>'), chipResultTypeMap[resultType]);
    };

    return <StyledTreeItem itemId={itemIdValue} label={<FolderLabel labelText={treeValue.treeNodeName} labelVariant='small'
        color={color}
        passed={treeValue.testsPassed} info={treeValue.testsNotCompleted}
        warnings={treeValue.testsWarning} errors={treeValue.testsFailed}
        onChipClick={chipClickHandler} />}>
    </StyledTreeItem>
}